import React, { useState, useMemo, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Checkbox, 
  Paper,
  LinearProgress,
  Divider,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Collapse,
  Button, // Add Button import
  alpha // Import alpha function
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import NoteIcon from '@mui/icons-material/Note'; // Add NoteIcon import
import { useTasks } from '../hooks/useTasks';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import NotesModal from './NotesModal'; // Import the new component
import { useParams } from 'react-router-dom';
import { analytics } from '../services/analytics';
import { useAnalytics } from '../hooks/useAnalytics';
import { useAuth } from '../contexts/AuthContext';

// Update the Task interface to include category
interface Task {
  id: string;
  areaName: string;
  summary: string;
  details: string;
  category: string;
  notes?: string;
}

// Update the TasksListProps interface
interface TasksListProps {
  isMobileModal?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}

const TaskItem = React.memo(({ 
  task, 
  isCompleted, 
  isExpanded, 
  onToggleTask, 
  onToggleExpand,
  onUpdateTask
}: {
  task: Task;
  isCompleted: boolean;
  isExpanded: boolean;
  onToggleTask: (id: string) => void;
  onToggleExpand: (id: string) => void;
  onUpdateTask: (taskId: string, areaName: string, taskSummary: string, updates: any) => void;
}) => {
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  
  const handleOpenNotesModal = () => {
    setNotesModalOpen(true);
  };
  
  const handleCloseNotesModal = () => {
    setNotesModalOpen(false);
  };
  
  const handleSaveNotes = (newNotes: string) => {
    onUpdateTask(task.id, task.areaName, task.summary, { notes: newNotes });
  };
  
  const getCategoryIcon = useCallback((category: string) => {
    switch(category) {
      case 'Warning':
        return <WarningIcon fontSize="small" color="warning" />;
      case 'Photo':
        return <PhotoCameraIcon fontSize="small" color="info" />;
      case 'Informational':
      default:
        return <InfoIcon fontSize="small" color="primary" />;
    }
  }, []);

  const hasNotes = Boolean(task.notes && task.notes.trim().length > 0);

  return (
    <React.Fragment>
      <ListItem 
        sx={{ 
          py: 1.5,
          px: 2,
          ml: 0,  // Ensure left margin is 0
          mr: 0,  // Ensure right margin is 0
          borderBottom: '1px solid',
          borderColor: 'divider',
          '&:last-of-type': isExpanded ? {
            borderBottom: isExpanded ? '1px solid' : 'none',
            borderColor: 'divider',
          } : {
            borderBottom: 'none'
          },
          // Change the background color to a light success color for completed tasks
          bgcolor: isCompleted ? 'rgba(76, 175, 80, 0.08)' : 'inherit',
          transition: 'background-color 0.3s'
        }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          <Checkbox
            edge="start"
            checked={isCompleted}
            icon={<RadioButtonUncheckedIcon />}
            checkedIcon={<CheckCircleIcon color="success" />}
            onChange={() => onToggleTask(task.id)}
            sx={{ p: 0.5 }}
          />
        </ListItemIcon>
        <ListItemText 
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* Always show the category icon regardless of completion status */}
              <Box sx={{ mr: 1 }}>
                {getCategoryIcon(task.category)}
              </Box>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontWeight: 500,
                  textDecoration: isCompleted ? 'line-through' : 'none',
                  color: isCompleted ? 'text.secondary' : 'text.primary'
                }}
              >
                {task.summary}
              </Typography>
              {/* Show note icon indicator if task has notes */}
              {hasNotes && (
                <NoteIcon 
                  fontSize="small" 
                  color="action" 
                  sx={{ ml: 1, opacity: 0.7 }}
                />
              )}
            </Box>
          }
          secondary={
            <Typography variant="caption" color="text.secondary">
              {task.category}
            </Typography>
          }
          sx={{ m: 0 }}
          onClick={() => onToggleExpand(task.id)}
        />
        <IconButton 
          size="small" 
          onClick={() => onToggleExpand(task.id)}
          aria-expanded={isExpanded}
          aria-label="show task details"
        >
          {isExpanded ? 
            <KeyboardArrowUpIcon fontSize="small" /> : 
            <KeyboardArrowDownIcon fontSize="small" />}
        </IconButton>
      </ListItem>
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box sx={{ 
          px: 4, 
          py: 1.5, 
          // Match the new completed task color if the task is completed
          bgcolor: isCompleted ? 'rgba(76, 175, 80, 0.08)' : 'background.default',
          borderBottom: '1px solid',
          borderColor: 'divider',
          '&:last-child': {
            borderBottom: 'none'
          },
          transition: 'background-color 0.3s'
        }}>
          <Typography variant="body2" color="text.secondary">
            {task.details}
          </Typography>
          
          {/* Simplified Notes section */}
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2">
                Notes
              </Typography>
              <Button 
                size="small" 
                onClick={handleOpenNotesModal}
                startIcon={hasNotes ? <EditIcon /> : <AddIcon />}
                color="primary"
              >
                {hasNotes ? 'Edit Notes' : 'Add Notes'}
              </Button>
            </Box>
            
            {/* Preview of notes if they exist */}
            {hasNotes && (
              <Box 
                sx={{
                  mt: 1,
                  p: 1.5,
                  bgcolor: theme => alpha(theme.palette.background.paper, 0.5),
                  borderRadius: 1,
                  borderLeft: '3px solid',
                  borderColor: 'primary.main',
                  maxHeight: '80px',
                  overflow: 'hidden',
                  position: 'relative'
                }}
              >
                <Typography 
                  variant="body2"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {task.notes}
                </Typography>
                {task.notes && task.notes.length > 100 && (
                  <Box 
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      left: 0,
                      height: '24px',
                      background: 'linear-gradient(to bottom, transparent, rgba(255,255,255,0.8))'
                    }}
                  />
                )}
              </Box>
            )}
          </Box>
          
          {/* Notes Modal */}
          <NotesModal
            isOpen={notesModalOpen}
            onClose={handleCloseNotesModal}
            notes={task.notes || ''}
            onSave={handleSaveNotes}
            taskSummary={task.summary}
          />
        </Box>
      </Collapse>
    </React.Fragment>
  );
});

const TasksList = ({ isMobileModal = false, isExpanded = false, onToggleExpand }: TasksListProps) => {
  const { 
    tasks: apiTasks, 
    isLoading, 
    completedTaskIds, 
    toggleTaskCompletionWithServer,
    updateTask
  } = useTasks();
  
  const { homeId } = useParams<{ homeId: string }>();
  const { user } = useAuth();
  
  const [expandedArea, setExpandedArea] = useState<string | null>(null);
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());

  // Log tasks_list_open analytics event
  useAnalytics(
    () => {
      if (user?.uuid && homeId) {
        analytics.logTasksListOpen(user.uuid, homeId);
      }
    },
    [homeId, user?.uuid]
  );
  
  // Memoize the processed tasks to prevent recreation when apiTasks reference hasn't changed
  const tasks = useMemo(() => {
    if (!apiTasks || apiTasks.length === 0) return [];
    
    return apiTasks.map(apiTask => ({
      id: apiTask.id,
      areaName: apiTask.area_name,
      summary: apiTask.summary,
      details: apiTask.details || '',
      category: apiTask.category || 'Informational',
      notes: apiTask.notes || ''
    }));
  }, [apiTasks]);

  const handleToggleTask = useCallback((taskId: string) => {
    const task = apiTasks.find(t => t.id === taskId);
    if (task) {
      toggleTaskCompletionWithServer(taskId, task.area_name, task.summary);
    }
  }, [apiTasks, toggleTaskCompletionWithServer]);

  const isTaskCompleted = useCallback((taskId: string) => 
    completedTaskIds.has(taskId), [completedTaskIds]);

  const handleToggleArea = useCallback((areaName: string) => {
    setExpandedArea(prev => prev === areaName ? null : areaName);
  }, []);

  const handleToggleTaskExpand = useCallback((taskId: string) => {
    setExpandedTasks(prevExpanded => {
      const newExpanded = new Set(prevExpanded);
      if (newExpanded.has(taskId)) {
        newExpanded.delete(taskId);
      } else {
        newExpanded.add(taskId);
      }
      return newExpanded;
    });
  }, []);

  // Update the completedTasksCount calculation
  const completedTasksCount = completedTaskIds.size;
  const progressPercentage = tasks.length ? (completedTasksCount / tasks.length) * 100 : 0;

  // Memoize tasksByArea
  const tasksByArea = useMemo(() => {
    return tasks.reduce((acc, task) => {
      if (!acc[task.areaName]) {
        acc[task.areaName] = [];
      }
      acc[task.areaName].push(task);
      return acc;
    }, {} as Record<string, Task[]>);
  }, [tasks]);

  // Memoize areaNames
  const areaNames = useMemo(() => {
    return Object.keys(tasksByArea).sort();
  }, [tasksByArea]);

  // Memoize areaProgress
  const areaProgress = useMemo(() => {
    return Object.entries(tasksByArea).reduce((acc, [areaName, areaTasks]) => {
      const totalTasks = areaTasks.length;
      const completedTasks = areaTasks.filter(task => completedTaskIds.has(task.id)).length;
      const percentage = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
      
      acc[areaName] = {
        total: totalTasks,
        completed: completedTasks,
        percentage
      };
      
      return acc;
    }, {} as Record<string, { total: number; completed: number; percentage: number }>);
  }, [tasksByArea, completedTaskIds]);

  const getRemainingCategoryCounts = useCallback((areaTasks: Task[]) => {
    const counts = {
      Warning: 0,
      Informational: 0,
      Photo: 0
    };
    
    areaTasks.forEach(task => {
      if (!completedTaskIds.has(task.id) && counts.hasOwnProperty(task.category)) {
        counts[task.category as keyof typeof counts]++;
      }
    });
    
    return counts;
  }, [completedTaskIds]);

  const getCategoryColor = useCallback((category: string) => {
    switch(category) {
      case 'Warning':
        return 'warning.main';
      case 'Photo':
        return 'info.main';
      case 'Informational':
      default:
        return 'primary.main';
    }
  }, []);

  const getCategoryIcon = useCallback((category: string) => {
    switch(category) {
      case 'Warning':
        return <WarningIcon fontSize="small" color="warning" />;
      case 'Photo':
        return <PhotoCameraIcon fontSize="small" color="info" />;
      case 'Informational':
      default:
        return <InfoIcon fontSize="small" color="primary" />;
    }
  }, []);

  const handleUpdateTask = useCallback((
    taskId: string, 
    areaName: string, 
    taskSummary: string, 
    updates: any
  ) => {
    updateTask(taskId, areaName, taskSummary, updates);
  }, [updateTask]);

  return (
    <Paper 
      elevation={1}
      sx={{ 
        mb: 0,
        overflow: 'hidden',
        borderTopRightRadius: isMobileModal ? 0 : 2, // Remove border radius in modal
        borderTopLeftRadius: isMobileModal ? 0 : 2, // Remove border radius in modal
        borderBottomRightRadius: 0,
        borderBottomLeftRadius: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.panel',
      }}
    >
      {/* Only show header when not in mobile modal */}
      {!isMobileModal && (
        <>
          <Box sx={{ borderRadius: 0, p: 2, bgcolor: 'background.header', color: 'primary.contrastText' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                Home Buying Tasks
              </Typography>
              {onToggleExpand && (
                <IconButton 
                  onClick={onToggleExpand}
                  size="small"
                  color="inherit"
                  aria-label={isExpanded ? "collapse tasks" : "expand tasks"}
                >
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Box sx={{ flexGrow: 1, mr: 2 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={progressPercentage} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    bgcolor: 'rgba(255,255,255,0.3)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: 'success.light'
                    }
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                {completedTasksCount}/{tasks.length} completed
              </Typography>
            </Box>
          </Box>
          
          <Divider />
        </>
      )}
      
      {/* Show progress info on mobile modal */}
      {isMobileModal && !isLoading && tasks.length > 0 && (
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ flexGrow: 1, mr: 2 }}>
            <LinearProgress 
              variant="determinate" 
              value={progressPercentage} 
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {completedTasksCount}/{tasks.length} completed
          </Typography>
        </Box>
      )}
      
      {isLoading ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">Loading tasks...</Typography>
        </Box>
      ) : tasks.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No tasks available. Chat with the AI to analyze this home and generate tasks.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {areaNames.map(areaName => (
            <Accordion 
              key={areaName}
              expanded={expandedArea === areaName}
              onChange={() => handleToggleArea(areaName)}
              disableGutters
              elevation={0}
              sx={{
                '&:before': { display: 'none' },
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    flexGrow: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                  <Typography fontWeight={500}>{areaName}</Typography>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    ml: 'auto',
                    mr: 1,
                    gap: 1
                  }}>
                    {/* Category indicators */}
                    {Object.entries(getRemainingCategoryCounts(tasksByArea[areaName])).map(([category, count]) => (
                      count > 0 && (
                        <Chip 
                          key={category}
                          size="small"
                          icon={getCategoryIcon(category)}
                          label={count}
                          sx={{ 
                            height: 24,
                            bgcolor: `${getCategoryColor(category)}`,
                            color: 'white',
                            '& .MuiChip-label': {
                              px: 1
                            }
                          }}
                        />
                      )
                    ))}
                    
                    {/* Total completion chip */}
                    <Chip 
                      size="small"
                      label={`${areaProgress[areaName].completed}/${areaProgress[areaName].total}`}
                      sx={{ 
                        height: 24,
                        bgcolor: areaProgress[areaName].percentage === 100 ? 'success.light' : 'primary.light',
                        color: areaProgress[areaName].percentage === 100 ? 'success.contrastText' : 'primary.contrastText',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ flexGrow: 1, mr: 2 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={areaProgress[areaName].percentage} 
                        sx={{ 
                          height: 6,
                          borderRadius: 3
                        }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {Math.round(areaProgress[areaName].percentage)}%
                    </Typography>
                  </Box>
                </Box>
                <List dense sx={{ py: 0, px: 0 }}> {/* Removed horizontal padding */}
                  {tasksByArea[areaName].map((task) => (
                    <TaskItem 
                      key={task.id}
                      task={task}
                      isCompleted={isTaskCompleted(task.id)}
                      isExpanded={expandedTasks.has(task.id)}
                      onToggleTask={handleToggleTask}
                      onToggleExpand={handleToggleTaskExpand}
                      onUpdateTask={handleUpdateTask}
                    />
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Paper>
  );
};

export default TasksList;